/** Angular */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
/** Sixense */
import { BydModel } from '@sixense/byd-core-service';
/** Services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** Models */
import { PinFilterInterface, PinFilterModel } from '@sixense/ds-pins-models';
/** Constants */
import { DS_PIN_BASE_URL } from '../constants/ds-pin-base-url.constant';

@Injectable({
  providedIn: 'root',
})
export class DsPinsFiltersService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get all filters (user filters and shared filters).
   *
   * @param projectId Project ID
   * @param pinId ID of the pin
   * @returns Observable<BydModel<PinFilterModel>>
   */
  public getFilters(projectId: number): Observable<BydModel<PinFilterModel>> {
    this.path = `${DS_PIN_BASE_URL}${projectId}/pins/filters`;
    return this.getModels<PinFilterModel>();
  }

  /**
   * Add a new pin filter.
   *
   * @param projectId the project id
   * @param payload Payload to create the filter
   * @returns Observable<PinFilterModel>
   */
  public addFilter(projectId: number, payload: PinFilterModel): Observable<PinFilterModel> {
    this.path = `${DS_PIN_BASE_URL}${projectId}/pins/filters`;
    return this.postModel<PinFilterModel>({ body: payload });
  }

  /**
   * Update a pin filter.
   *
   * @param projectId the project id
   * @param filterId Filter id
   * @param payload Payload to create the filter
   * @returns Observable<PinFilterModel>
   */
  public updateFilter(projectId: number, filterId: number, payload: PinFilterModel): Observable<PinFilterModel> {
    this.path = `${DS_PIN_BASE_URL}${projectId}/pins/filters/${filterId}`;
    return this.putModel<PinFilterModel>({ body: payload });
  }

  /**
   * Delete a pin filter.
   *
   * @param projectId the project id
   * @param filterId Filter id
   * @param payload Payload to create the filter
   * @returns Observable<boolean>
   */
  public deleteFilter(projectId: number, filterId: number): Observable<boolean> {
    this.path = `${DS_PIN_BASE_URL}${projectId}/pins/filters/${filterId}`;
    return this.delete();
  }

  /**
   * Share/unshare a pin filter.
   *
   * @param projectId the project id
   * @param filterId Filter id
   * @param shared The shared state to set
   * @returns Observable<PinFilterModel>
   */
  public shareFilter(projectId: number, filterId: number, shared: boolean): Observable<PinFilterModel> {
    this.path = `${DS_PIN_BASE_URL}${projectId}/pins/filters/${filterId}/share`;
    return this.putModel<PinFilterModel>({ body: { shared } });
  }

  /**
   * Create PinFilterModel instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `PinFilterModel` instance.
   */
  public createNewInstance(jsonObject: PinFilterInterface): PinFilterModel {
    return new PinFilterModel(jsonObject);
  }
}
